import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NodeProps, NodeResizer, useStoreApi } from 'reactflow';
import { useBoardMultiSelected } from '../useBoardSelection';
import type { BoardTextData } from '../../../types';
import { useBoardActions } from '../boardActions';
import { BOARD_TEXT_STYLES, BOARD_TEXT_FONT_SIZES } from '../boardConstants';
import NodeActionsToolbar from './NodeActionsToolbar';

/** Inline styles for the current preset; legacy nodes (no preset) keep CSS defaults. */
function resolveTextStyle(data: BoardTextData): React.CSSProperties {
  const preset = BOARD_TEXT_STYLES.find((s) => s.id === data.textStyle);
  if (!preset) {
    return { fontSize: data.fontSize || 18, color: data.color || '#1f2937' };
  }
  return {
    fontSize: data.fontSize || preset.fontSize,
    fontWeight: preset.fontWeight,
    lineHeight: preset.lineHeight,
    letterSpacing: preset.letterSpacing,
    textTransform: preset.textTransform,
    color: data.color || preset.color || '#1f2937',
  };
}

const TextNode: React.FC<NodeProps<BoardTextData>> = ({ id, data, selected }) => {
  const { updateNodeData, readOnly } = useBoardActions();
  const store = useStoreApi();
  const [editing, setEditing] = useState(false);
  const multiSelected = useBoardMultiSelected();
  const [draft, setDraft] = useState(data.text || '');
  const draftRef = useRef(draft);
  const savedRef = useRef(data.text || '');
  const originRef = useRef(data.text || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  draftRef.current = draft;
  savedRef.current = data.text || '';

  const persist = useCallback(
    (next: string) => {
      draftRef.current = next;
      setDraft(next);
      if (next !== savedRef.current) {
        savedRef.current = next;
        updateNodeData(id, { text: next });
      }
    },
    [id, updateNodeData],
  );

  const flush = useCallback(() => {
    persist(draftRef.current);
  }, [persist]);

  useEffect(() => {
    if (!selected || multiSelected) {
      flush();
      setEditing(false);
    }
  }, [selected, multiSelected, flush]);

  useEffect(() => {
    if (selected && !readOnly && !multiSelected) {
      setEditing((was) => {
        if (!was) originRef.current = draftRef.current;
        return true;
      });
    }
  }, [selected, readOnly, multiSelected]);

  useEffect(() => {
    if (!editing) setDraft(data.text || '');
  }, [data.text, editing]);

  useEffect(() => {
    if (!editing || !textareaRef.current) return;
    const el = textareaRef.current;
    el.focus();
    const len = el.value.length;
    if (len === 0) el.select();
    else el.setSelectionRange(len, len);
  }, [editing]);

  const style = resolveTextStyle(data);

  return (
    <>
      <NodeResizer
        isVisible={!!selected}
        minWidth={80}
        minHeight={40}
        lineClassName="board-resize-line"
        handleClassName="board-resize-handle"
      />
      <NodeActionsToolbar
        id={id}
        showTextStyles
        textStyle={data.textStyle}
        showFontSize
        fontSize={Number(style.fontSize)}
        fontSizeOptions={BOARD_TEXT_FONT_SIZES}
      />
      <div
        className={`board-node board-text-node ${selected ? 'is-selected' : ''}`}
        onDoubleClick={() => {
          if (readOnly) return;
          store.getState().addSelectedNodes([id]);
          setEditing(true);
        }}
      >
        {editing ? (
          <textarea
            ref={textareaRef}
            className="board-text-textarea nodrag"
            style={style}
            value={draft}
            onChange={(e) => persist(e.target.value)}
            onBlur={flush}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                persist(originRef.current);
                setEditing(false);
              }
            }}
          />
        ) : (
          <div className="board-text-content" style={style}>
            {data.text || <span className="board-node-placeholder">Double-click to edit</span>}
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(TextNode);
